import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  UserGroupIcon,
  MicrophoneIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  CurrencyDollarIcon,
  LanguageIcon,
  CalendarDaysIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

// Simplified navigation
const navigation = [
  { name: 'My Family', href: '/recipients', icon: UserGroupIcon },
  { name: 'Voice Clone', href: '/voice-clone', icon: MicrophoneIcon },
  { name: 'My Podcasts', href: '/podcast-library', icon: MicrophoneIcon },
  { name: 'Deployment Plans', href: '/pricing', icon: CurrencyDollarIcon },
];

export default function Navigation() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [language, setLanguage] = useState('en');
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  useEffect(() => {
    if (user) {
      loadLanguagePreference();
      loadSupportedLanguages();
    }
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLanguageMenu && !event.target.closest('.language-menu-container')) {
        setShowLanguageMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageMenu]);

  const loadLanguagePreference = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/${user.id}/language-preference`);
      setLanguage(response.data.language_code || 'en');
    } catch (error) {
      console.error('Failed to load language preference:', error);
    }
  };

  const loadSupportedLanguages = async () => {
    try {
      const response = await axios.get(`${API_URL}/languages/supported`);
      console.log('Loaded languages:', response.data.languages);
      setSupportedLanguages(response.data.languages || {});
    } catch (error) {
      console.error('Failed to load supported languages:', error);
      // Set default languages if API fails
      setSupportedLanguages({
        'en': 'English',
        'es': 'Spanish',
        'fr': 'French',
        'de': 'German',
        'it': 'Italian',
        'pt': 'Portuguese',
        'zh': 'Chinese',
        'ja': 'Japanese',
        'ko': 'Korean',
        'ar': 'Arabic',
        'hi': 'Hindi'
      });
    }
  };

  const saveLanguagePreference = async (langCode) => {
    try {
      await axios.post(`${API_URL}/user/language-preference`, {
        user_id: user.id,
        language_code: langCode
      });
      setLanguage(langCode);
      setShowLanguageMenu(false);
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  };

  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-2xl font-bold text-primary-600 hover:text-primary-700">
                Always Here
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-3 py-4 border-b-4 text-sm font-semibold ${
                      isActive
                        ? 'border-primary-600 text-gray-900'
                        : 'border-transparent text-gray-600 hover:border-gray-400 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className="h-5 w-5 mr-2" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
          
          {/* User menu */}
          <div className="flex items-center space-x-2">
            {/* Contact Link */}
            <Link
              to="/contact"
              className="inline-flex items-center px-2 py-2 text-xs font-medium text-gray-700 hover:text-gray-900"
            >
              <EnvelopeIcon className="h-4 w-4 mr-1" />
              Contact
            </Link>
            
            {user ? (
              <>
                {/* Language Selector */}
                <div className="relative language-menu-container">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Language button clicked, current state:', showLanguageMenu);
                      console.log('Supported languages:', supportedLanguages);
                      setShowLanguageMenu(!showLanguageMenu);
                    }}
                    className="inline-flex items-center px-2 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    type="button"
                  >
                    <LanguageIcon className="h-4 w-4 mr-1" />
                    <span className="hidden md:inline">{supportedLanguages[language] || 'English'}</span>
                  </button>
                  
                  {showLanguageMenu && (
                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[100] max-h-96 overflow-y-auto">
                      <div className="py-1">
                        {Object.keys(supportedLanguages).length === 0 ? (
                          <div className="px-4 py-2 text-sm text-gray-500">Loading languages...</div>
                        ) : (
                          Object.entries(supportedLanguages).map(([code, name]) => (
                            <button
                              key={code}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Selected language:', code, name);
                                saveLanguagePreference(code);
                              }}
                              type="button"
                              className={`block w-full text-left px-4 py-2 text-sm ${
                                language === code
                                  ? 'bg-primary-50 text-primary-700 font-medium'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {name}
                              {language === code && ' ✓'}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center text-xs text-gray-700">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-6 w-6 rounded-full mr-1" />
                  ) : (
                    <UserCircleIcon className="h-6 w-6 mr-1 text-gray-400" />
                  )}
                  <span className="font-medium hidden md:inline">{user.name || user.email}</span>
                </div>
                <button
                  onClick={logout}
                  className="inline-flex items-center px-2 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">Logout</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-lg shadow-lg text-white bg-primary-600 hover:bg-primary-700"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
